<template>
  <div id="app">
    <div class="machine">
      <Header :title="info.TITLE" :description="info.DESCRIPTION" />

      <main class="machine__main">
        <div class="machine__info">
          <Instructions />

          <Controls
            :gameState="gameState"
            :score="score"
            @toggleGameState="toggleGameState"
          />
        </div>

        <div class="machine__divider"></div>

        <ul class="machine__keys">
          <li v-for="(key, idx) in keys" :key="key.id" class="machine__key">
            <Key
              :id="key.id"
              :idx="idx"
              :labelUpper="(idx + 1).toString()"
              :labelLower="key.name"
              labelledBy="lower"
              emitEvent="keyHit"
              :isActive="activeKey === idx"
              :isPressed="pressedKey === idx"
              @keyHit="handleKeyHit"
            />
          </li>
        </ul>
      </main>
    </div>

    <p class="credits" v-html="info.CREDITS"></p>
  </div>
</template>

<script>
import { GAME, INFO, KEYS } from '@/data/game.js'

import Controls from '@/components/Controls.vue'
import Header from '@/components/Header.vue'
import Instructions from '@/components/Instructions.vue'
import Key from '@/components/Key.vue'

import '@/assets/styles/main.scss'

export default {
  name: 'App',
  components: {
    Controls,
    Header,
    Instructions,
    Key,
  },
  data() {
    return {
      gameState: -1,
      score: 0,
      interval: GAME.INTERVAL,
      delay: GAME.DELAY,
      levelInterval: GAME.LEVEL_INTERVAL,
      activeKey: null,
      prevKey: null,
      pressedKey: null,
      keyTimer: null,
      nextKeyTimer: null,
      audioLoaded: false,
      audioPlayed: false,
    }
  },
  created() {
    this.keys = KEYS
    this.info = INFO
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('keyup', this.handleKeyUp)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('keyup', this.handleKeyUp)
    this.audioCtx.close()
  },
  watch: {
    score(val) {
      // Decrease the duration of keys every `levelInterval` points
      if (val >= this.levelInterval && val % this.levelInterval === 0) {
        this.interval = Math.floor(this.interval * 0.9)
      }
    },
  },
  methods: {
    // Game
    toggleGameState() {
      this.gameState === -1 ? this.startGame() : this.restartGame()
    },
    startGame() {
      this.gameState = 1
      if (!this.audioLoaded) {
        this.initAudio()
      }
      this.activateKey()
    },
    restartGame() {
      this.resetKey()
      this.resetState()
      this.score = 0
      this.gameState = 1
      this.activateNextKey()
    },
    endGame() {
      this.resetKey()
      this.resetState()
      this.gameState = 0
    },

    // Resets
    resetState() {
      this.interval = GAME.INTERVAL
      this.prevKey = null
      this.keyTimer = null
    },
    resetKey() {
      this.activeKey = null
      this.audioPlayed = false
      clearTimeout(this.keyTimer)
    },

    // Keys
    activateKey() {
      this.activeKey = this.getRandomKey()
      // End the game if this timeout isn't cleared
      this.keyTimer = setTimeout(() => {
        this.endGame()
      }, this.interval)
    },
    activateNextKey() {
      // Prevent triggering a stale key if restart button is spammed
      clearTimeout(this.nextKeyTimer)
      this.nextKeyTimer = setTimeout(() => {
        this.activateKey()
      }, this.delay)
    },
    getRandomKey() {
      const rand = Math.floor(Math.random() * this.keys.length)
      if (rand === this.prevKey) return this.getRandomKey()
      this.prevKey = rand
      return rand
    },

    // Event Handlers
    handleKeyHit(idx, id) {
      // Initialize audio if a key is pressed before game start
      if (!this.audioLoaded) {
        this.initAudio()
      }

      if (this.activeKey === idx) {
        this.score++
        // Allow playing audio only once per key
        if (!this.audioPlayed) {
          this.playAudio(id)
          this.audioPlayed = true
        }
        this.resetKey()
        this.activateNextKey()
      } else if (this.gameState !== 1) {
        // Play drum machine freely when not in-game
        this.playAudio(id)
      }
    },
    handleKeyDown(e) {
      if (e.repeat) return
      if (+e.key >= 1 && +e.key <= this.keys.length) {
        const idx = +e.key - 1
        this.handleKeyHit(idx, this.keys[idx].id)
        this.pressedKey = idx
      }
    },
    handleKeyUp() {
      if (this.pressedKey >= 0) this.pressedKey = null
    },

    // Audio
    initAudio() {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      this.audioCtx = new AudioContext()
      this.audioBuffers = {}
      this.keys.forEach((key) => {
        fetch(require(`@/assets/audio/${key.path}`))
          .then((res) => res.arrayBuffer())
          .then((buffer) => {
            this.audioCtx.decodeAudioData(buffer, (decodedData) => {
              this.audioBuffers[key.id] = decodedData
            })
          })
      })
      this.audioLoaded = true
    },
    playAudio(id) {
      const bufferSource = this.audioCtx.createBufferSource()
      bufferSource.buffer = this.audioBuffers[id]
      bufferSource.connect(this.audioCtx.destination)
      bufferSource.start(0)
    },
  },
}
</script>
